import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Heading, Logo, TextInput } from '@restworld/ui-ds-base';

import { ManualEntryPrompt } from '../shared/manual-venue-entry';
import { SERVER_STEP_ONE_HOLD_CLASS, SERVER_STEP_ONE_HOLD_SCRIPT } from './server-step-one-hold';

/**
 * Step 1 of /getting-started as the server draws it: logo, title, subtitle and the venue search
 * field, in the HTML, before any JavaScript runs (RW-235, item 1).
 *
 * **Why a separate component and not the real step.** The real onboarding reads its state from
 * localStorage on its very first render (`atomWithStorage` with `getOnInit`), so the server cannot
 * render it: a returning visitor's first client render would differ from the HTML and React would
 * throw the hydration away. This component reads nothing from the browser, so the server and the
 * first client render always agree. Once auth and the router are ready the page swaps it for the
 * real onboarding, whose code ships in the same chunk (no `dynamic`, no `React.lazy` for step 1),
 * so the swap happens in the same tick. The markup mirrors the real step class by class, so the
 * swap does not move anything on screen.
 *
 * **Who must not see it.** The page is static (`getStaticProps`): the same HTML answers every
 * URL, so the server cannot tell `?ref=` from a plain visit. A visitor with a code, a stored
 * session or onboarding progress saved in this browser goes through today's flow (lookup, intro,
 * homepage redirect, resume modal), and for them this step must never be painted. The inline
 * script below runs while the HTML is parsed, before the first paint, and hides the shell for
 * them (see `server-step-one-hold.ts`). React never touches the class it sets (it lives on
 * `<html>`), so hydration is unaffected.
 */

interface ServerStepOneProps {
  /** Text typed before the real step took over, so the swap keeps it. */
  onInputChange: (value: string) => void;
  inputRef: React.Ref<HTMLInputElement>;
}

export const ServerStepOne: React.FunctionComponent<ServerStepOneProps> = ({ onInputChange, inputRef }) => {
  const { t } = useTranslation('restaurant-onboarding');

  // The class only exists to hide this shell. Dropping it when the shell goes means a later
  // client-side visit to /getting-started in the same session is judged afresh.
  React.useEffect(
    () => () => {
      document.documentElement.classList.remove(SERVER_STEP_ONE_HOLD_CLASS);
    },
    []
  );

  return (
    <>
      {/* First in the shell, so it runs before the browser parses (and paints) what follows. */}
      <script dangerouslySetInnerHTML={{ __html: SERVER_STEP_ONE_HOLD_SCRIPT }} />
      {/* Mirrors LayoutWrapper (hideNavigation) > OrganizationOnboarding > RestaurantCreationStep
          > RestaurantOnboarding > GoogleAddressInputResult, on the empty search screen. Tailwind
          only: styled-components styles are not collected on the server in this app. */}
      <div data-testid="server-step-one" className="[.rw-gs-hold_&]:invisible">
        <div className="w-full bg-neutral-10 min-h-screen xl:flex">
          <div className="w-full">
            <div className="w-full px-4 py-3 self-stretch border-b border-neutral-40 inline-flex justify-center fixed top-0 bg-neutral-10 [z-index:99]">
              <div className="w-full flex flex-row items-center justify-between max-w-[640px] px-0 sm:px-4">
                <Logo variant="horizontal" title={t('restworld_logo_alt')} className="h-8 w-auto text-purple-700" />
                <div />
              </div>
            </div>
            <div className="min-h-screen bg-neutral-10">
              <div className="w-full">
                <div className="pt-[80px] pb-[100px] flex items-center justify-center min-h-screen px-4">
                  <div className="w-full flex flex-row items-center justify-center md:mx-auto md:max-w-[38rem] md:min-w-[38rem] h-full py-0">
                    <div className="w-full" style={{ transform: 'translateY(4%)' }}>
                      <div className="flex flex-col w-full h-full gap-6">
                        <div className="self-start flex flex-col justify-start gap-1">
                          <Heading size={4} color="neutral">
                            {t('let_s_get_started')}
                          </Heading>
                          <Heading size={4} color="neutral">
                            {t('which_venue_are_you_looking_to_hire_staff_for')}
                          </Heading>
                          <p className="text-base font-normal text-neutral-500 m-0 mt-2">
                            {t('let_s_get_started_subtitle')}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 w-full">
                          <div className="w-full">
                            <div className="w-full flex flex-col gap-8">
                              <TextInput
                                ref={inputRef}
                                type="text"
                                id="search-address-input"
                                placeholder={t('venue_search_placeholder')}
                                icon={MagnifyingGlassIcon}
                                bordercolor="primary"
                                hasLeftPadding
                                isFullwidth
                                wrapperStyle="w-full"
                                autoComplete="off"
                                onChange={onInputChange}
                              />
                            </div>
                          </div>
                        </div>
                        {/* The real step shows it from the start too, so the swap adds nothing under the field. The real step replaces this one within moments, so its click does nothing. */}
                        <ManualEntryPrompt visible onClick={() => undefined} />
                      </div>
                    </div>
                  </div>
                  <div className="w-full py-3 bg-neutral-10 inline-flex justify-center items-center fixed bottom-0 left-0 right-0 z-[99]">
                    {/* The year is fixed when the static HTML is built. Across a new year the client would
                        render a different one; the real step replaces this line moments later anyway. */}
                    <p className="text-center text-sm font-medium text-neutral-400" suppressHydrationWarning>
                      {t('copyright_restworld', { year: new Date().getFullYear() })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServerStepOne;
